// src/component/Test/index.jsx

import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Typography, Box, Card, Radio } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../Api/instance";
import { UserContext } from "../../Context";
import { PageHeader } from "../ui/PageHeaders";
import { PrimaryButton, SecondaryButton } from "../ui/Buttons";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VerifiedIcon from "@mui/icons-material/Verified";
import { styled } from "@mui/material/styles";

const TestPageContainer = styled(Box)(({ theme }) => ({
  minHeight: "85vh",
  padding: "32px 0",
  backgroundColor: theme.palette.background.default,
}));

const QuestionCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  padding: "32px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  minHeight: "550px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
}));

const SidebarCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  padding: "32px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
}));

const OptionContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected'
})(({ theme, selected }) => ({
  display: "flex",
  alignItems: "center",
  padding: "16px 20px",
  borderRadius: "16px",
  border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: selected ? `${theme.palette.primary.main}0D` : "transparent",
  marginBottom: "16px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: selected ? theme.palette.primary.main : theme.palette.text.secondary,
    backgroundColor: selected ? `${theme.palette.primary.main}0D` : theme.palette.action.hover,
  },
}));

const ImageBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "16px",
  overflow: "hidden",
  backgroundColor: theme.palette.action.hover,
  padding: "16px",
  marginBottom: "24px",
  cursor: "zoom-in",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
}));

function Test() {
  const { sno, paperid } = useParams();
  const [data, setData] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const email = localStorage.getItem("userMail");
  const { Endpoint } = useContext(UserContext);
  const [count, setCount] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionNumbers, setQuestionNumbers] = useState([]);
  const [answers, setAnswers] = useState([]);
  const navigate = useNavigate();

  const handleFinish = () => {
    navigate("/year");
  };

  const handleAnswerClick = (answer) => {
    const updatedAnswers = [...answers];
    updatedAnswers[count] = answer;
    setAnswers(updatedAnswers);
    setSelectedAnswer(answer);
    setShowCorrectAnswer(true);
  };

  useEffect(() => {
    fetchTotalQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, sno, paperid]);

  useEffect(() => {
    if (questionNumbers.length > 0) {
      fetchData(questionNumbers[count]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, questionNumbers]);

  const fetchTotalQuestions = async () => {
    try {
      const response = await axiosInstance.post(`get/U_ViewPMCQCount.php`, {
        userId: email,
        institutionId: sno,
        paperId: paperid,
      });
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
          return;
        }
        if (Array.isArray(response.data)) {
          const snoList = response.data.map((item) => item.sno);
          setQuestionNumbers(snoList);
          setTotalQuestions(snoList.length);
          setAnswers(Array(snoList.length).fill(""));
          if (snoList.length > 0) {
            fetchData(snoList[0]);
          }
        } else {
          console.error("Data received from API is not an array:", response.data);
        }
      }
    } catch (error) {
      console.error("Error fetching total questions:", error);
    }
  };

  const fetchData = async (questionId) => {
    try {
      const response = await axiosInstance.post(`get/U_ViewPmcqQuestions.php`, {
        userId: email,
        institutionId: sno,
        paperId: paperid,
        questionId: questionId,
      });
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
          return;
        }
        if (Array.isArray(response.data)) {
          setData(response.data);
        } else {
          console.error("Data received from API is not an array:", response.data);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleNext = () => {
    if (count < totalQuestions - 1) {
      setCount((prevCount) => prevCount + 1);
      setSelectedAnswer(answers[count + 1] || "");
      setShowCorrectAnswer(answers[count + 1] ? true : false);
    }
  };

  const handlePrevious = () => {
    if (count > 0) {
      setCount((prevCount) => prevCount - 1);
      setSelectedAnswer(answers[count - 1] || "");
      setShowCorrectAnswer(true);
    }
  };

  const [zoomImage, setZoomImage] = useState(false);
  const handleImageClick = () => {
    setZoomImage(!zoomImage);
  };

  const isImage = (url) => {
    return url && url.match(/\.(jpeg|jpg|gif|png|svg|webp|jfif)$/) != null;
  };

  return (
    <TestPageContainer>
      <Container>
        {data.length > 0 && (
          <Box key={data[0].sno}>
            
            {/* Custom Header */}
            <PageHeader 
              title="Practice Examination Panel" 
              subtitle={`Question Progress: ${count + 1} of ${totalQuestions}`}
              onBack={() => navigate(-1)}
            />

            <Row className="g-4">
              
              {/* LEFT COLUMN: QUESTION CONTENT */}
              <Col xs={12} lg={8}>
                <QuestionCard>
                  <Box>
                    {/* Question text */}
                    <Typography 
                      variant="h2" 
                      style={{ fontWeight: 700, marginBottom: "24px", lineHeight: 1.5 }}
                    >
                      {count + 1}. {data[0].questions}
                    </Typography>

                    {/* Question Image (if any) */}
                    {data[0].image && (
                      <ImageBox onClick={handleImageClick}>
                        <img
                          src={`${Endpoint}/controllers/api/admin/upload/${data[0].image}`}
                          alt="Question Illustration"
                          style={{
                            maxHeight: zoomImage ? "350px" : "180px",
                            maxWidth: "100%",
                            objectFit: "contain",
                            transform: zoomImage ? "scale(1.08)" : "scale(1)",
                            transition: "all 0.3s ease",
                          }}
                        />
                      </ImageBox>
                    )}

                    {/* Options Selectors */}
                    <Box style={{ marginTop: "16px" }}>
                      {["option1", "option2", "option3", "option4"].map((optionKey, index) => {
                        const optionText = data[0][optionKey];
                        const isSelected = selectedAnswer === optionText;
                        
                        return (
                          <OptionContainer
                            key={index}
                            selected={isSelected}
                            onClick={() => handleAnswerClick(optionText)}
                          >
                            <Radio
                              checked={isSelected}
                              value={optionText}
                              color="primary"
                              style={{ padding: "0 12px 0 0" }}
                            />
                            {isImage(optionText) ? (
                              <img
                                src={optionText}
                                alt={`Option ${index + 1}`}
                                style={{ maxHeight: "70px", borderRadius: "8px" }}
                              />
                            ) : (
                              <Typography variant="body1" style={{ fontWeight: 550 }}>
                                {optionText}
                              </Typography>
                            )}
                          </OptionContainer>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Previous / Next buttons */}
                  <Box style={{ display: "flex", gap: "16px", marginTop: "32px" }}>
                    <SecondaryButton
                      disabled={count === 0}
                      onClick={handlePrevious}
                      startIcon={<ArrowBackIcon />}
                      style={{ height: "46px", borderRadius: "14px", minWidth: "120px" }}
                    >
                      Previous
                    </SecondaryButton>
                    <PrimaryButton
                      disabled={count >= totalQuestions - 1}
                      onClick={handleNext}
                      endIcon={<ArrowForwardIcon />}
                      style={{ height: "46px", borderRadius: "14px", minWidth: "120px" }}
                    >
                      Next Question
                    </PrimaryButton>
                  </Box>
                </QuestionCard>
              </Col>

              {/* RIGHT COLUMN: CORRECT ANSWER INFO & SUBMISSION */}
              <Col xs={12} lg={4}>
                <SidebarCard>
                  <Box>
                    <Box style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
                      <HelpOutlineIcon color="primary" />
                      <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                        Self-Evaluation Panel
                      </Typography>
                    </Box>

                    {showCorrectAnswer ? (
                      <Box style={{ padding: "20px", borderRadius: "16px", backgroundColor: "rgba(16, 185, 129, 0.08)", border: "1px solid rgba(16, 185, 129, 0.2)", display: "flex", flexDirection: "column", gap: "12px" }}>
                        <Box style={{ display: "flex", alignItems: "center", gap: "8px", color: "var(--color-success)" }}>
                          <VerifiedIcon />
                          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                            Correct Answer
                          </Typography>
                        </Box>
                        {isImage(data[0].answer) ? (
                          <img
                            src={`${Endpoint}/controllers/api/admin/upload/${data[0].answer}`}
                            alt="Answer representation"
                            style={{ maxHeight: "80px", objectFit: "contain", borderRadius: "8px" }}
                          />
                        ) : (
                          <Typography variant="body1" style={{ fontWeight: 600, color: "var(--color-text-primary)" }}>
                            {data[0].answer}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box style={{ padding: "24px", borderRadius: "16px", border: "1px dashed var(--color-border)", textAlign: "center" }}>
                        <Typography variant="body2" color="textSecondary">
                          Select an option to evaluate and view the correct answer instantly.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box style={{ marginTop: "40px" }}>
                    <PrimaryButton
                      onClick={handleFinish}
                      style={{
                        width: "100%",
                        height: "48px",
                        borderRadius: "16px",
                        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                        boxShadow: "0 4px 12px rgba(5, 150, 105, 0.2)",
                        fontWeight: 700,
                      }}
                    >
                      Finish Test
                    </PrimaryButton>
                  </Box>
                </SidebarCard>
              </Col>

            </Row>

          </Box>
        )}
      </Container>
    </TestPageContainer>
  );
}

export default Test;
